import { CLUSTER_COLUMN_FILTER_NAME, EColumnFilterType } from "@/models/filter.model";
import { ECustomCell, type IStatusColOptions, type ITableColumn } from "@/models/table.model";
import { AssetSyncStatusEnum } from "@/swagger-models/assets-service-client";
import {
  type DatavolumeNoSharedScopes,
  DatavolumePhase,
  type DatavolumeStatus,
} from "@/swagger-models/datavolumes-service-client";
import { assetsUtil } from "@/utils/assets.util/assets.util";
import { dateUtil } from "@/utils/date.util";
import { getFieldContent } from "@/utils/table-format.util";

export const allDataVolumeColumnsMap: Record<string, ITableColumn> = {
  dataVolume: {
    name: "name",
    label: "Data volume",
    field: (row: DatavolumeNoSharedScopes) => row.name,
    sortable: true,
    align: "left",
    customCell: ECustomCell.NAME_COL,
  },
  description: {
    name: "description",
    label: "Description",
    field: (row: DatavolumeNoSharedScopes) => row.description,
    format: (val: string | null | undefined) => val || "-",
    align: "left",
    display: false,
    hideFilter: true,
    customCell: ECustomCell.DESCRIPTION_COL,
  },
  status: {
    name: "status",
    label: "Status",
    field: (row: DatavolumeNoSharedScopes): DatavolumeStatus => row.status,
    format(status: DatavolumeStatus, row: DatavolumeNoSharedScopes): IStatusColOptions {
      let assetStatus: AssetSyncStatusEnum;
      switch (status.phase) {
        case DatavolumePhase.Ready:
          assetStatus = AssetSyncStatusEnum.NoIssuesFound;
          break;
        case DatavolumePhase.NotReady:
          assetStatus = AssetSyncStatusEnum.IssuesFound;
          break;
        case DatavolumePhase.Creating:
          assetStatus = AssetSyncStatusEnum.Creating;
          break;
        case DatavolumePhase.Deleting:
          assetStatus = AssetSyncStatusEnum.Deleting;
          break;
        default:
          assetStatus = AssetSyncStatusEnum.NoStatus;
          break;
      }

      return assetsUtil.getStatusColOptions({ status: assetStatus, message: status.phaseMessage }, row.createdAt || "");
    },
    hideFilter: true,
    sort: (a, b, rowA, rowB) => {
      const valB: IStatusColOptions = getFieldContent(allDataVolumeColumnsMap.status, rowB);
      const valA: IStatusColOptions = getFieldContent(allDataVolumeColumnsMap.status, rowA);
      return valA.status.toLowerCase().localeCompare(valB.status.toLowerCase());
    },
    customCell: ECustomCell.STATUS_COL,
    align: "left",
  },
  scope: {
    name: "scope",
    label: "Scope",
    field: (row: DatavolumeNoSharedScopes): string => row.projectId,
    format: () => "View",
    align: "left",
    customCell: ECustomCell.LINK_COL,
    hideFilter: true,
    customCellEvent: { emitName: "scope-col-clicked" },
  },
  originPvc: {
    name: "origin-pvc",
    label: "Origin PVC",
    field: (row: DatavolumeNoSharedScopes) => row.originPvcName,
    hideFilter: true,
    align: "left",
  },
  originProject: {
    name: "origin-project",
    label: "Origin project",
    field: (row: DatavolumeNoSharedScopes) => row.projectName,
    hideFilter: true,
    align: "left",
  },
  cluster: {
    name: CLUSTER_COLUMN_FILTER_NAME,
    label: "Cluster",
    field: (row: DatavolumeNoSharedScopes) => row.clusterId,
    sortable: true,
    align: "left",
    customCell: ECustomCell.CLUSTER_ID_TO_NAME_COL,
    hideFilter: true,
  },
  createdBy: {
    name: "createdBy",
    label: "Created by",
    field: (row: DatavolumeNoSharedScopes) => row.createdBy,
    sortable: true,
    align: "left",
  },
  createdAt: {
    name: "createdAt",
    label: "Creation time",
    field: (row: DatavolumeNoSharedScopes) => row.createdAt,
    sortable: true,
    align: "left",
    filter: {
      type: EColumnFilterType.Date,
    },
    format: (createdAt: string) => dateUtil.dateAndTimeFormat(new Date(createdAt)),
  },
  lastUpdated: {
    name: "last-updated",
    label: "Last updated",
    field: (row: DatavolumeNoSharedScopes) => row.updatedAt,
    align: "left",
    hideFilter: true,
    format: (updatedAt: string) => dateUtil.dateAndTimeFormat(new Date(updatedAt)),
  },
};

export const allDataVolumeColumns: Array<ITableColumn> = [
  allDataVolumeColumnsMap.dataVolume,
  allDataVolumeColumnsMap.description,
  allDataVolumeColumnsMap.status,
  allDataVolumeColumnsMap.scope,
  allDataVolumeColumnsMap.originPvc,
  allDataVolumeColumnsMap.originProject,
  allDataVolumeColumnsMap.createdBy,
  allDataVolumeColumnsMap.createdAt,
  allDataVolumeColumnsMap.cluster,
];

export const dataVolumeIndexColumns: Array<ITableColumn> = [
  { ...allDataVolumeColumnsMap.dataVolume, display: true, mandatory: true },
  { ...allDataVolumeColumnsMap.description, display: false },
  { ...allDataVolumeColumnsMap.status, display: true },
  { ...allDataVolumeColumnsMap.scope, display: true },
  { ...allDataVolumeColumnsMap.originPvc, display: true },
  { ...allDataVolumeColumnsMap.originProject, display: false },
  { ...allDataVolumeColumnsMap.cluster, display: true },
  { ...allDataVolumeColumnsMap.createdBy, display: true },
  { ...allDataVolumeColumnsMap.createdAt, display: true },
  { ...allDataVolumeColumnsMap.lastUpdated, display: false },
];
